import React, { createContext, useContext, ReactNode, useMemo } from 'react';
import { useFeedbackList as useApiFeedbackList, useCreateFeedback, useUpdateFeedback, useDeleteFeedback } from '../hooks/useFeedbackApi';
import { useFeedbackInstances } from '../hooks/useFeedbackInstanceApi';
import { FeedbackInstance as ApiFeedbackInstance } from '../services/feedbackInstanceService';

export type QuestionType = 'text' | 'multipleChoice' | 'checkbox' | 'rating';

export interface Question {
  id: string;
  type: QuestionType;
  text: string;
  required: boolean;
  options?: string[];
  ratingScale?: number;
}

export interface Feedback {
  id: string;
  title: string;
  description: string;
  questions: Question[];
  questionCount: number;
  createdBy: string;
  createdAt: Date;
  status?: string;
  isPublished?: boolean;
  publishedVersions?: number[];
}

export type AssigneeType = 'user' | 'group' | 'role';

export interface FeedbackInstance {
  id: string;
  feedbackId: string;
  assignedTo: string[];
  assigneeType: AssigneeType;
  frequency: 'once' | 'daily' | 'weekly' | 'monthly';
  startDate: Date;
  endDate?: Date;
  status: 'scheduled' | 'active' | 'completed';
  hasUserResponded?: boolean;
}

export interface FeedbackResponse {
  id: string;
  instanceId: string;
  userId: string;
  answers: {
    questionId: string;
    answer: string | string[];
  }[];
  submittedAt: Date;
}

interface FeedbackContextType {
  feedbackList: Feedback[];
  instances: FeedbackInstance[];
  responses: FeedbackResponse[];
  loading: boolean;
  addFeedback: (feedbackItem: Omit<Feedback, 'id' | 'createdAt'>) => Promise<string>;
  updateFeedback: (feedbackItem: Feedback) => Promise<void>;
  deleteFeedback: (id: string) => Promise<void>;
  getFeedback: (id: string) => Feedback | undefined;
  getInstance: (id: string) => FeedbackInstance | undefined;
  getUserResponses: (userId: string) => FeedbackResponse[];
}

const FeedbackContext = createContext<FeedbackContextType | undefined>(undefined);

export const useFeedback = () => {
  const context = useContext(FeedbackContext);
  if (!context) {
    throw new Error('useFeedback must be used within a FeedbackProvider');
  }
  return context;
};

interface FeedbackProviderProps {
  children: ReactNode;
}

function adaptApiToFeedback(apiFeedbackItem: any): Feedback {
  return {
    id: apiFeedbackItem.id,
    title: apiFeedbackItem.title || apiFeedbackItem.name || '',
    description: apiFeedbackItem.description || '',
    questions: [],
    questionCount: apiFeedbackItem.questionCount || 0,
    createdBy: apiFeedbackItem.createdBy || '',
    createdAt: new Date(apiFeedbackItem.createdAt),
    status: apiFeedbackItem.status,
    isPublished: apiFeedbackItem.isPublished,
    publishedVersions: apiFeedbackItem.publishedVersions,
  };
}

function adaptApiToFeedbackInstance(apiInstance: ApiFeedbackInstance): FeedbackInstance {
  let assignedToArray: string[] = [];

  if (Array.isArray(apiInstance.assignedTo)) {
    assignedToArray = apiInstance.assignedTo;
  } else if (apiInstance.assignedTo && typeof apiInstance.assignedTo === 'object') {
    const assignedToObj = apiInstance.assignedTo as any;
    if (assignedToObj.userIds && Array.isArray(assignedToObj.userIds)) {
      assignedToArray = assignedToObj.userIds;
    } else if (Object.values(assignedToObj).length > 0) {
      const firstValue = Object.values(assignedToObj)[0];
      if (Array.isArray(firstValue)) {
        assignedToArray = firstValue;
      }
    }
  }

  return {
    id: apiInstance.id,
    feedbackId: apiInstance.feedbackId,
    assignedTo: assignedToArray,
    assigneeType: apiInstance.assigneeType as AssigneeType,
    frequency: apiInstance.frequency as 'once' | 'daily' | 'weekly' | 'monthly',
    startDate: new Date(apiInstance.startDate),
    endDate: apiInstance.endDate ? new Date(apiInstance.endDate) : undefined,
    status: apiInstance.status as 'scheduled' | 'active' | 'completed',
  };
}

function toArray<T>(value: T | T[] | null | undefined | { items?: T[]; data?: T[] }): T[] {
  if (!value) return [];
  if (Array.isArray(value)) return value;
  if (typeof value === 'object' && 'items' in value && Array.isArray(value.items)) return value.items;
  if (typeof value === 'object' && 'data' in value && Array.isArray(value.data)) return value.data;
  return [];
}

export const FeedbackProvider: React.FC<FeedbackProviderProps> = ({ children }) => {
  const { data: apiFeedback, isLoading: feedbackLoading } = useApiFeedbackList();
  const { data: apiInstances, isLoading: instancesLoading } = useFeedbackInstances();

  const createFeedbackMutation = useCreateFeedback();
  const updateFeedbackMutation = useUpdateFeedback();
  const deleteFeedbackMutation = useDeleteFeedback();

  const feedbackList = useMemo(
    () => toArray(apiFeedback).map(adaptApiToFeedback),
    [apiFeedback]
  );

  const instances = useMemo(
    () => toArray(apiInstances).map(adaptApiToFeedbackInstance),
    [apiInstances]
  );

  const responses: FeedbackResponse[] = [];

  const addFeedback = async (feedbackItem: Omit<Feedback, 'id' | 'createdAt'>): Promise<string> => {
    const result = await createFeedbackMutation.mutateAsync({
      title: feedbackItem.title,
      status: 'draft',
    });
    return result.id;
  };

  const updateFeedback = async (feedbackItem: Feedback): Promise<void> => {
    await updateFeedbackMutation.mutateAsync({
      id: feedbackItem.id,
      dto: {
        title: feedbackItem.title,
        status: 'draft',
      },
    });
  };

  const deleteFeedback = async (id: string): Promise<void> => {
    await deleteFeedbackMutation.mutateAsync(id);
  };

  const getFeedback = (id: string): Feedback | undefined => {
    return feedbackList.find((s) => s.id === id);
  };

  const getInstance = (id: string): FeedbackInstance | undefined => {
    return instances.find((i) => i.id === id);
  };

  const getUserResponses = (userId: string): FeedbackResponse[] => {
    return responses.filter((r) => r.userId === userId);
  };

  const value = {
    feedbackList,
    instances,
    responses,
    loading: feedbackLoading || instancesLoading,
    addFeedback,
    updateFeedback,
    deleteFeedback,
    getFeedback,
    getInstance,
    getUserResponses,
  };

  return <FeedbackContext.Provider value={value}>{children}</FeedbackContext.Provider>;
};
